import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CountUp, Delta, delay, Empty, formatDay, human, Spark, STAGGER, ToolIcon, useTheme, useToast } from '@caios/ui';
import { authClient, gate, type Me } from '../auth.ts';
import { ApproveAllFixes, FixButtons, fixIdOf } from '../components/fix-decision.tsx';
import './pocket.css';

/**
 * Pocket: the owner's iPad view (Adam 2026-10-30). What is waiting on him, then his prospects and his clients as logo
 * tiles; a client opens on its documents (read, mark up with Pencil, approve or send back to Claude) and its numbers.
 *
 * The lock is real, not a curtain: locking SIGNS OUT, and unlocking is a passkey sign-in — on an iPad that is Face ID or
 * Touch ID. So a locked Pocket holds no session at all. It locks on the button, after 5 minutes untouched, and when the
 * app comes back after more than a minute in the background.
 */
const STAFF = ['owner', 'admin', 'manager'];
const IDLE_MS = 5 * 60_000, AWAY_MS = 60_000;
const UNLOCKED = 'caios.pocket.unlockedAt';

type Item = { id: string; kind: string; title: string; body: string | null; link: string; created_at: string; read_at: string | null };
type Task = { id: string; title: string; status: string; category: string; priority: string; due_on: string | null; list: string };
type TaskFull = { task: { id: string; title: string; description: string; account_id: string }; comments: { id: string; body: string; created_at: string }[] };
type Handoff = { id: string; note: string; status: string; reply: string | null; created_at: string };
type Report = { id: string; account_id: string; account: string; type: string; audience: 'client' | 'team'; title: string; period_label: string; period_end: string; status: 'draft' | 'published'; revision: string | null; updated_at: string };
type Figures = { sessions: number | null; clicks: number | null; impressions: number | null; spend: number | null; adConversions: number | null; leads: number | null };
type Standing = { id: string; name: string; sources: string[]; syncedAt: string | null; leadsFrom: 'site' | 'ga4' | null; now: Figures; previous: Figures; series: Record<'sessions' | 'clicks' | 'spend' | 'leads', (number | null)[]> };
type Standings = { window: { from: string; to: string; days: number }; clients: Standing[] };
type Tab = 'today' | 'needs' | 'prospects' | 'clients' | `org:${string}`;
type Partner = { id: string; name: string };
type Account = { id: string; name: string; relationship: string; status: string; logo: string | null };
type Deal = { id: string; title: string; organization: string | null; website: string | null; contact_name: string | null; value: string | null; monthly: string; one_off: string;
  next_action: string | null; next_action_at: string | null; expected_close_on: string | null; stage: string; stage_color: string; stage_position: number; stage_changed_at: string; account_id: string | null; files: number };
type DealFile = { id: string; deal_id: string; title: string; filename: string; mime: string; size: number; source: string; created_at: string; deal?: string };
/** Where a client opens: the tile grid, then one client's documents or numbers (Adam 2026-10-30: "a list of client logos and names first"). */
type Open = { kind: 'client' | 'prospect'; id: string; view: 'docs' | 'numbers' } | null;

/**
 * A white-label partner (6 Hands Media, Live Echo) is its own org; Adam owns it too. Pocket reads a partner's org beside
 * ARC's without switching the session: a read names it in `?org=` (an iframe or an <img> can't send headers), a write in
 * the `x-caios-org` header. null = the session's own org.
 */
type Org = string | null;
const inOrg = (path: string, org: Org) => (org ? `${path}${path.includes('?') ? '&' : '?'}org=${org}` : path);
async function api<T>(path: string, init?: RequestInit, org: Org = null): Promise<T> {
  const headers = { ...(init?.body ? { 'content-type': 'application/json' } : {}), ...(org ? { 'x-caios-org': org } : {}) };
  const r = await fetch(path, { credentials: 'include', ...init, headers });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.error ?? `Request failed (${r.status})`);
  return body as T;
}
const send = <T,>(method: string, path: string, body: unknown = {}, org: Org = null) => api<T>(path, { method, body: JSON.stringify(body) }, org);
/** "6 Hands Media" and "6Hands Media" are one name. */
const same = (a: string, b: string) => a.toLowerCase().replace(/[^a-z0-9]/g, '') === b.toLowerCase().replace(/[^a-z0-9]/g, '');
const age = (iso: string) => { const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000); return m < 60 ? `${Math.max(1, m)}m` : m < 1440 ? `${Math.round(m / 60)}h` : `${Math.round(m / 1440)}d`; };
const ymd = (d: Date) => d.toISOString().slice(0, 10);
const windowOf = (days: number) => { const to = new Date(Date.now() - 86400000); return { from: ymd(new Date(to.getTime() - (days - 1) * 86400000)), to: ymd(to) }; };
const REPORT: Record<string, string> = { 'monthly-seo': 'SEO report', 'monthly-seo-internal': 'SEO, internal', 'monthly-ads': 'Ads report', 'monthly-ads-internal': 'Ads, internal',
  'proofboard-period': 'Growth evidence', 'proofboard-period-internal': 'Growth evidence, internal', 'weekly-paid-media': 'Paid media, weekly', 'monthly-paid-media': 'Paid media, monthly',
  'market-paid-media-monthly': 'Paid media, one location', 'competitor-intel': 'Competitor intel', 'local-authority-map': 'Local authority map',
  'onboarding-tracking-setup': 'Onboarding: tracking set-up', 'onboarding-data-sources': 'Onboarding: data sources', 'onboarding-access-checklist': 'Onboarding: access checklist',
  'onboarding-seo-setup': 'Onboarding: SEO set-up', 'onboarding-playbook-90': 'Onboarding: 90-day playbook', 'onboarding-build-manual': 'Onboarding: build manual',
  'onboarding-exec-brief': 'Onboarding: executive brief', 'onboarding-build-sheets': 'Onboarding: build sheets' };
const reportName = (t: string) => REPORT[t] ?? (t.startsWith('teardown') ? 'Competitor teardown' : human(t.replace(/-/g, ' ')));
/** What kind of document it is, for the filters: onboarding packs, sales material, or a client report. */
type DocKind = 'onboarding' | 'sales' | 'report';
const docKind = (t: string): DocKind => (t.startsWith('onboarding-') ? 'onboarding' : t === 'competitor-intel' || t.startsWith('teardown') || t === 'local-authority-map' ? 'sales' : 'report');
/** Only a client's draft is published from here; the team's onboarding drafts are read and marked up, never published. */
const canPublish = (r: Report) => r.audience === 'client' && r.status === 'draft' && !r.revision;
const toReview = (r: Report) => canPublish(r) || (docKind(r.type) === 'onboarding' && r.status === 'draft' && !r.revision);
/**
 * A task needs you now when it is due within a week, at most a week late, or in progress. Older overdue plan steps
 * (most came over from Manifest) and later work stay in Tasks (Adam 2026-10-30).
 */
const weekAgo = () => ymd(new Date(Date.now() - 7 * 86400000));
const isNow = (t: Task) => (t.due_on ? t.due_on >= weekAgo() && ymd(new Date(Date.now() + 7 * 86400000)) >= t.due_on : t.category === 'active');
const KIND_LABEL: Record<DocKind, string> = { onboarding: 'Onboarding', sales: 'Sales', report: 'Report' };
/** "19-25 October 2026, compared with 12-18 October 2026" reads as "19-25 October 2026" on a card. */
const shortPeriod = (p: string) => p.split(/,\s*compared with/i)[0]!;
function Status({ r }: { r: Report }) {
  return r.revision ? <span className="cx-chip cx-chip--decision">With Claude</span> : toReview(r) ? <span className="cx-chip cx-chip--warn">To review</span>
    : r.status === 'published' ? <span className="cx-chip cx-chip--good">{r.audience === 'team' ? 'Final' : 'Published'}</span> : <span className="cx-chip">Team draft</span>;
}
const money = (n: number) => `$${Math.round(n).toLocaleString()}`;
const KIND: Record<string, string> = { approval: 'Approval', assigned: 'Assigned', mentioned: 'Mention', due: 'Due today', system: 'CAIOS', ticket: 'Support', booking: 'Booking', incident: 'Monitoring' };

/** One round button: the shell's Tide/Arctic switch is too wide for the iPad rail. */
function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  return (
    <button type="button" className="pk-round" onClick={() => setTheme(theme === 'tide' ? 'arctic' : 'tide')} aria-label={theme === 'tide' ? 'Switch to light' : 'Switch to dark'}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="4.5" /><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
    </button>
  );
}

export function Pocket() {
  const [me, setMe] = useState<Me | null>(null);
  const [state, setState] = useState<'checking' | 'locked' | 'open' | 'refused'>('checking');
  const [signedIn, setSignedIn] = useState<Me | null>(null);
  const qc = useQueryClient();

  const lock = useCallback(async () => {
    setState('locked'); setMe(null); setSignedIn(null);
    try { sessionStorage.removeItem(UNLOCKED); } catch { /* storage blocked */ }
    qc.clear();                                   // nothing from the last session stays in memory behind the lock
    await authClient.signOut().catch(() => {});
  }, [qc]);

  // Open only with a fresh unlock in this tab; anything else starts at the lock, even with a session from elsewhere.
  useEffect(() => { void (async () => {
    let fresh = false;
    try { fresh = Date.now() - Number(sessionStorage.getItem(UNLOCKED) ?? 0) < IDLE_MS; } catch { /* storage blocked */ }
    const g = await gate();
    if (g.kind === 'ok' && !STAFF.includes(g.me.role)) return setState('refused');
    if (g.kind === 'ok' && fresh) { setMe(g.me); setState('open'); return; }
    // Signed in some other way on this device (password + code): the lock can register this iPad's Face ID.
    setSignedIn(g.kind === 'ok' ? g.me : null);
    setState('locked');
  })(); }, []);

  // Idle and background locks.
  useEffect(() => {
    if (state !== 'open') return;
    let idle = setTimeout(lock, IDLE_MS), hiddenAt = 0;
    const touch = () => { clearTimeout(idle); idle = setTimeout(lock, IDLE_MS); try { sessionStorage.setItem(UNLOCKED, String(Date.now())); } catch { /* storage blocked */ } };
    const vis = () => { if (document.hidden) hiddenAt = Date.now(); else if (hiddenAt && Date.now() - hiddenAt > AWAY_MS) void lock(); else touch(); };
    addEventListener('pointerdown', touch); addEventListener('keydown', touch); document.addEventListener('visibilitychange', vis);
    return () => { clearTimeout(idle); removeEventListener('pointerdown', touch); removeEventListener('keydown', touch); document.removeEventListener('visibilitychange', vis); };
  }, [state, lock]);

  if (state === 'checking') return <div className="cx-app pk-lock" />;
  if (state === 'refused') return <div className="cx-app pk-lock"><div className="pk-lock-card"><b>Pocket is for owners and managers.</b><a href="/">Back to CAIOS</a></div></div>;
  if (state === 'locked' || !me) return <Lock signedIn={signedIn} onOpen={(m) => { try { sessionStorage.setItem(UNLOCKED, String(Date.now())); } catch { /* storage blocked */ } setMe(m); setState('open'); }} />;
  return <PocketApp onLock={lock} />;
}

function Lock({ signedIn, onOpen }: { signedIn: Me | null; onOpen: (me: Me) => void }) {
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 15_000); return () => clearInterval(t); }, []);
  async function unlock() {
    setBusy(true); setError(null);
    const r = await authClient.signIn.passkey();
    if (r?.error) {
      setBusy(false);
      // 401 = the device offered a passkey CAIOS has never seen (e.g. one saved when staging used this address).
      return setError(r.error.status === 401
        ? signedIn ? 'CAIOS doesn’t know the passkey this iPad offered. Tap “Set up Face ID on this iPad” below.' : 'CAIOS doesn’t know the passkey this iPad offered. Sign in another way once, then set up Face ID here.'
        : 'Not unlocked. Try Face ID again.');
    }
    const g = await gate();
    setBusy(false);
    if (g.kind !== 'ok') return setError(g.kind === 'no-org' ? 'This passkey has no organisation in CAIOS.' : 'Signed in, but CAIOS wants another step first. Open CAIOS to finish it.');
    if (!STAFF.includes(g.me.role)) return setError('Pocket is for owners and managers.');
    setOk(true); setTimeout(() => onOpen(g.me), 450);
  }
  /** Register this device's passkey (Face ID / Touch ID) on the session it already has — same as Security → Add a passkey. */
  async function enrol() {
    if (!signedIn) return;
    setBusy(true); setError(null);
    const r = await authClient.passkey.addPasskey({ name: /iPad|Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1 ? 'iPad (Pocket)' : 'Pocket passkey' });
    setBusy(false);
    if (r?.error) return setError('Face ID wasn’t set up. Try again.');
    setOk(true); setTimeout(() => onOpen(signedIn), 450);
  }
  return (
    <div className="cx-app pk-lock" data-testid="pocket-lock">
      <div className="pk-lock-card cx-rise">
        <div className="pk-lock-time">{now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).replace(/\s?[AP]M/, '')}</div>
        <div className="pk-lock-date">{now.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}</div>
        <button type="button" className={`pk-face${busy ? ' is-scan' : ''}${ok ? ' is-ok' : ''}`} onClick={unlock} disabled={busy} aria-label="Unlock with Face ID" data-testid="pocket-unlock">
          <svg viewBox="0 0 64 64" fill="none" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <g className="pk-face-id"><path d="M8 20V14a6 6 0 0 1 6-6h6M44 8h6a6 6 0 0 1 6 6v6M56 44v6a6 6 0 0 1-6 6h-6M20 56h-6a6 6 0 0 1-6-6v-6" /><path d="M22 25v4M42 25v4M32 25v12h-3M24 44c5 4 11 4 16 0" /></g>
            <path className="pk-face-ok" d="M18 33l9 9 19-20" />
          </svg>
        </button>
        <b>{busy ? 'Looking for you…' : 'Unlock CAIOS'}</b>
        {error ? <p className="auth-error" role="alert">{error}</p>
          : <p className="pk-hint">Face ID or Touch ID, through your passkey. Locking signs you out, so nothing is open behind this screen.</p>}
        {signedIn
          ? <button type="button" className="cx-btn" onClick={enrol} disabled={busy} data-testid="pocket-enrol">Set up Face ID on this iPad</button>
          : <a className="cx-link-btn" href="/signin?next=/pocket" data-testid="pocket-signin">Sign in another way</a>}
      </div>
    </div>
  );
}

function PocketApp({ onLock }: { onLock: () => void }) {
  const [tab, setTab] = useState<Tab>('today');
  const [openReport, setOpenReportIn] = useState<{ id: string; org: Org } | null>(() => { const id = new URLSearchParams(location.search).get('report'); return id ? { id, org: null } : null; });
  const setOpenReport = (id: string) => setOpenReportIn({ id, org: null });
  const qc = useQueryClient();
  const [task, setTask] = useState<string | null>(null);
  const [open, setOpen] = useState<Open>(null);
  const [days, setDays] = useState(30);
  const session = authClient.useSession();

  // A Home Screen web app keeps its first copy of the page: look for a newer build when Pocket comes back to the
  // front (and every 10 minutes), and reload onto it.
  useEffect(() => {
    const mine = document.querySelector<HTMLScriptElement>('script[src*="/assets/index-"]')?.getAttribute('src');
    if (!mine) return;                                   // the dev server: nothing to compare
    const check = async () => {
      if (document.hidden) return;
      const html = await fetch('/pocket', { cache: 'no-store' }).then((r) => r.text()).catch(() => '');
      const live = html.match(/\/assets\/index-[^"]+\.js/)?.[0];
      if (live && live !== mine) location.reload();
    };
    const t = setInterval(check, 10 * 60_000);
    document.addEventListener('visibilitychange', check);
    void check();
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', check); };
  }, []);

  const inbox = useQuery({ queryKey: ['pk-inbox'], queryFn: () => api<Item[]>('/api/v1/inbox?view=open'), refetchInterval: 30_000 });
  const tasks = useQuery({ queryKey: ['pk-work'], queryFn: () => send<Task[]>('POST', '/api/v1/ops/work', {}), refetchInterval: 60_000 });
  const reports = useQuery({ queryKey: ['pk-reports'], queryFn: () => api<Report[]>('/api/v1/delivery/reports'), refetchInterval: 60_000 });
  const win = useMemo(() => windowOf(days), [days]);
  const standings = useQuery({ queryKey: ['pk-standings', win.from, win.to], queryFn: () => api<Standings>(`/api/v1/delivery/standings?from=${win.from}&to=${win.to}`) });
  const accounts = useQuery({ queryKey: ['pk-accounts'], queryFn: () => api<Account[]>('/api/v1/clients'), staleTime: 5 * 60_000 });
  const deals = useQuery({ queryKey: ['pk-deals'], queryFn: () => api<Deal[]>('/api/v1/sales/deals/open'), staleTime: 60_000 });
  // The white-label partners: every other org Adam is in (ARC's own org is the session's).
  const me = useQuery({ queryKey: ['pk-me'], queryFn: () => api<Me>('/api/v1/me'), staleTime: Infinity });
  const orgs = useQuery({ queryKey: ['pk-orgs'], queryFn: async () => (await authClient.organization.list()).data ?? [], staleTime: Infinity });
  const partners: Partner[] = (orgs.data ?? []).filter((o) => me.data && o.id !== me.data.orgId).map((o) => ({ id: o.id, name: o.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
  // ARC's books also hold each partner as an account (who pays ARC); in Pocket the partner is its own section instead.
  const direct = accounts.data?.filter((a) => !partners.some((p) => same(p.name, a.name)));

  const needs = (inbox.data?.length ?? 0) + (tasks.data?.filter(isNow).length ?? 0);
  const review = reports.data?.filter(toReview) ?? [];
  const go = (t: Tab) => { setTab(t); setOpen(null); };
  const account = (id: string | null | undefined) => accounts.data?.find((a) => a.id === id);
  const docsOf = (id: string | null | undefined) => (id ? (reports.data ?? []).filter((r) => r.account_id === id) : []);
  const first = (session.data?.user.name ?? '').split(' ')[0] || 'there';
  const hour = new Date().getHours();

  return (
    <div className="cx-app pk-app">
      <nav className="pk-rail" aria-label="Pocket">
        <img className="pk-brand" src="/caios.png" alt="CAIOS" />
        {([['today', 'owner-dashboard', 'Today', 0], ['needs', 'approvals', 'Needs you', needs], ['prospects', 'passage', 'Prospects', 0], ['clients', 'register', 'Clients', review.length]] as const).map(([id, icon, label, n], i) => (
          <div key={id} className="pk-rail-item">
            {id === 'prospects' && <hr className="pk-rail-rule" />}
            <button type="button" className="pk-rail-btn cx-rail-in" style={delay(i, STAGGER.rail)} aria-current={tab === id} aria-label={label} onClick={() => go(id)} data-testid={`pocket-tab-${id}`}>
              <ToolIcon name={icon} size={40} />{n > 0 && <span className="pk-badge" data-tone={id === 'clients' ? 'decision' : 'fire'}>{n}</span>}
            </button>
            <span className="pk-rail-label">{label}</span>
          </div>
        ))}
        {partners.map((p, i) => <PartnerRail key={p.id} p={p} first={i === 0} current={tab === `org:${p.id}`} onGo={() => go(`org:${p.id}`)} />)}
        <span className="pk-spacer" />
        <ThemeToggle />
        <button type="button" className="pk-round" onClick={onLock} aria-label="Lock now" data-testid="pocket-lock-now">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
        </button>
      </nav>
      <main className="pk-main">
        {tab === 'today' && <section className="cx-stack">
          <header className="pk-head"><div><h1>{hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'}, {first}</h1>
            <p className="pk-sub">{new Date().toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}</p></div></header>
          <div className="pk-stats">
            {[{ k: 'Needs you', v: needs, c: 'var(--cx-fire)', to: 'needs' as Tab },
              { k: 'Reports to review', v: review.length, c: 'var(--cx-decision)', to: 'clients' as Tab },
              { k: `Leads, ${days} days`, v: sum(standings.data?.clients, 'leads'), c: 'var(--cx-data-1)', to: 'clients' as Tab, before: sum(standings.data?.clients, 'leads', true) },
              { k: `Ad spend, ${days} days`, v: sum(standings.data?.clients, 'spend'), c: 'var(--cx-data-5)', to: 'clients' as Tab, money: true }].map((s, i) => (
              <button key={s.k} type="button" className="cx-stat pk-stat cx-rise" style={{ ['--c' as string]: s.c, ...delay(i, STAGGER.stats) }} onClick={() => go(s.to)}>
                <div className="cx-label">{s.k}</div><div className="cx-value"><CountUp value={s.v} {...(s.money ? { format: money } : {})} /></div>
                {s.before !== undefined && <Delta now={s.v} before={s.before} against={`the ${days} days before`} />}
              </button>))}
          </div>
          <div className="pk-two">
            <section className="cx-panel pk-card cx-rise" style={delay(1, STAGGER.tiles)}>
              <div className="pk-card-head"><h2>Waiting on you</h2><button type="button" className="cx-link-btn" onClick={() => go('needs')}>Open all</button></div>
              <NeedsList inbox={inbox.data ?? []} tasks={tasks.data ?? []} limit={6} onTask={(id) => { setTask(id); go('needs'); }} onItem={() => go('needs')} />
            </section>
            <div className="cx-stack">
              <section className="cx-panel pk-card cx-rise" style={delay(2, STAGGER.tiles)}>
                <div className="pk-card-head"><h2>Reports to review</h2><button type="button" className="cx-link-btn" onClick={() => go('clients')}>By client</button></div>
                {review.length ? review.slice(0, 4).map((r) => <DocRow key={r.id} r={r} onOpen={() => { setOpenReport(r.id); }} />) : <p className="cx-none">Nothing waiting to be checked.</p>}
              </section>
              <section className="cx-panel pk-card cx-rise" style={delay(3, STAGGER.tiles)}>
                <div className="pk-card-head"><h2>Biggest moves</h2><button type="button" className="cx-link-btn" onClick={() => go('clients')}>All clients</button></div>
                {movers(standings.data?.clients ?? []).map((c) => (
                  <button key={c.id} type="button" className="pk-row" onClick={() => { setTab('clients'); setOpen({ kind: 'client', id: c.id, view: 'numbers' }); }}>
                    <span className="pk-grow"><b>{c.name}</b><small>{(c.now.sessions ?? 0).toLocaleString()} sessions</small></span>
                    <Delta now={c.now.sessions ?? 0} before={c.previous.sessions ?? 0} against="before" />
                  </button>))}
                {standings.isSuccess && !movers(standings.data.clients).length && <p className="cx-none">No client has two windows of traffic to compare yet.</p>}
              </section>
            </div>
          </div>
        </section>}

        {tab === 'needs' && <Needs inbox={inbox.data ?? []} tasks={tasks.data ?? []} selected={task} onSelect={setTask} />}
        {tab === 'clients' && (open?.kind === 'client'
          ? <ClientPage key={open.id} a={account(open.id)} view={open.view} setView={(view) => setOpen({ ...open, view })} docs={docsOf(open.id)} c={standings.data?.clients.find((x) => x.id === open.id)}
              days={days} setDays={setDays} onBack={() => setOpen(null)} onReport={setOpenReport} />
          : <ClientGrid accounts={direct} reports={reports.data ?? []} loaded={accounts.isSuccess && reports.isSuccess} onOpen={(id) => setOpen({ kind: 'client', id, view: 'docs' })} />)}
        {tab === 'prospects' && (open?.kind === 'prospect'
          ? <ProspectPage key={open.id} d={deals.data?.find((x) => x.id === open.id)} a={account(deals.data?.find((x) => x.id === open.id)?.account_id)} docsOf={docsOf} onBack={() => setOpen(null)} onReport={setOpenReport} />
          : <ProspectGrid deals={deals.data} accounts={accounts.data ?? []} reports={reports.data ?? []} loaded={deals.isSuccess} onOpen={(id) => setOpen({ kind: 'prospect', id, view: 'docs' })} />)}
        {partners.map((p) => tab === `org:${p.id}` && <PartnerTab key={p.id} p={p} days={days} setDays={setDays} onReport={(id) => setOpenReportIn({ id, org: p.id })} />)}
      </main>
      {openReport && <Viewer id={openReport.id} org={openReport.org}
        report={(openReport.org ? qc.getQueryData<Report[]>(['pk-reports', openReport.org]) : reports.data)?.find((r) => r.id === openReport.id)}
        onClose={() => { setOpenReportIn(null); history.replaceState(null, '', '/pocket'); }} />}
    </div>
  );
}

const sum = (cs: Standing[] | undefined, k: keyof Figures, prev = false) => (cs ?? []).reduce((a, c) => a + ((prev ? c.previous : c.now)[k] ?? 0), 0);
const movers = (cs: Standing[]) => cs.filter((c) => (c.previous.sessions ?? 0) > 50)
  .sort((a, b) => Math.abs((b.now.sessions! - b.previous.sessions!) / b.previous.sessions!) - Math.abs((a.now.sessions! - a.previous.sessions!) / a.previous.sessions!)).slice(0, 4);

function DocRow({ r, onOpen }: { r: Report; onOpen: () => void }) {
  return (
    <button type="button" className="pk-row" onClick={onOpen} data-testid="pocket-doc-row">
      <span className="pk-thumb" data-kind={docKind(r.type)} />
      <span className="pk-grow"><b>{r.account} · {reportName(r.type)}</b><small>{r.period_label}</small></span>
      {r.revision ? <span className="cx-chip cx-chip--decision">With Claude</span> : toReview(r) ? <span className="cx-chip cx-chip--warn">To review</span> : r.status === 'published' ? <span className="cx-chip cx-chip--good">{r.audience === 'team' ? 'Final' : 'Published'}</span> : <span className="cx-chip">Team draft</span>}
    </button>
  );
}

function NeedsList({ inbox, tasks, limit, selected, onTask, onItem }: { inbox: Item[]; tasks: Task[]; limit?: number; selected?: string | null; onTask: (id: string) => void; onItem?: (i: Item) => void }) {
  const today = ymd(new Date());
  const [more, setMore] = useState(false);
  const now = tasks.filter(isNow), stale = tasks.filter((t) => !!t.due_on && weekAgo() > t.due_on).length;
  const rows = [
    ...inbox.map((i) => ({ key: `i${i.id}`, title: i.title, sub: i.body ?? human(i.kind), chip: KIND[i.kind] ?? human(i.kind), tone: i.kind === 'approval' ? 'fire' : 'decision', age: age(i.created_at), late: Date.now() - Date.parse(i.created_at) > 86400000, go: () => (onItem ? onItem(i) : onTask('')), id: i.id })),
    ...now.map((t) => ({ key: `t${t.id}`, title: t.title, sub: t.list, chip: 'Task', tone: 'accent', age: t.due_on ? (today > t.due_on ? 'Overdue' : t.due_on === today ? 'Today' : formatDay(`${t.due_on}T12:00:00`)) : '', late: !!t.due_on && today > t.due_on, go: () => onTask(t.id), id: t.id })),
  ];
  const cap = limit ?? (more ? rows.length : 60);
  if (!rows.length) return <p className="cx-none">Nothing is waiting on you.</p>;
  return <>{rows.slice(0, cap).map((r, n) => (
    <button key={r.key} type="button" className={`pk-row${n < 12 ? ' cx-rise' : ''}`} style={n < 12 ? delay(n, STAGGER.list) : undefined} aria-selected={selected === r.id} onClick={r.go} data-testid="pocket-need">
      <span className="pk-grow"><b>{r.title}</b><small>{r.sub}</small></span>
      <span className={`cx-chip cx-chip--${r.tone}`}>{r.chip}</span>{r.age && <span className={`cx-age${r.late ? ' cx-age--late' : ''}`}>{r.age}</span>}
    </button>))}
    {!limit && rows.length > cap && <button type="button" className="cx-link-btn pk-more" onClick={() => setMore(true)}>Show all {rows.length}</button>}
    {!limit && stale > 0 && <a className="cx-link-btn pk-more" href="/ops">{stale} older overdue task{stale === 1 ? '' : 's'} (over a week late) are in Tasks, not here</a>}</>;
}

function Needs({ inbox, tasks, selected, onSelect }: { inbox: Item[]; tasks: Task[]; selected: string | null; onSelect: (id: string | null) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [item, setItem] = useState<Item | null>(null);
  const current = selected || (item ? null : tasks[0]?.id ?? null);
  const done = useMutation({ mutationFn: (a: { id: string; action: 'done' | 'snooze' }) => send('POST', `/api/v1/inbox/${a.id}/${a.action}`, a.action === 'snooze' ? { until: 'later_today' } : {}),
    onSuccess: (_r, a) => { toast(a.action === 'done' ? <>Done · Inbox</> : <>Snoozed for 3 hours</>); setItem(null); qc.invalidateQueries({ queryKey: ['pk-inbox'] }); } });
  return (
    <section className="pk-needs">
      <header className="pk-head"><div><h1>Needs you</h1><p className="pk-sub">Answer it yourself, or hand it back to Claude with a note.</p></div>
        <ApproveAllFixes items={inbox} onDone={() => qc.invalidateQueries({ queryKey: ['pk-inbox'] })} /></header>
      {/* On a phone the list and the open task can't sit side by side: a pick opens it full screen, Back returns. */}
      <div className="pk-split" data-open={!!(selected || item) || undefined}>
        <div className="cx-panel pk-card pk-list"><NeedsList inbox={inbox} tasks={tasks} selected={item?.id ?? current} onTask={(id) => { setItem(null); onSelect(id); }} onItem={(i) => { onSelect(null); setItem(i); }} /></div>
        <div className="cx-panel pk-card pk-detail">
          {(selected || item) && <button type="button" className="cx-btn pk-detail-back" onClick={() => { setItem(null); onSelect(null); }} data-testid="pocket-task-back">‹ Needs you</button>}
          {item ? <div className="cx-stack" data-testid="pocket-item">
              <span className="cx-label">{human(item.kind)} · {age(item.created_at)} ago</span><h2>{item.title}</h2>
              {item.body && <p className="pk-ask">{item.body}</p>}
              <div className="pk-actions">
                {item.kind === 'approval' && fixIdOf(item.link) && <FixButtons link={item.link} itemId={item.id} onDecided={() => { setItem(null); qc.invalidateQueries({ queryKey: ['pk-inbox'] }); }} />}
                <a className={`cx-btn${fixIdOf(item.link) ? '' : ' cx-btn--primary'}`} href={item.link}>Open in CAIOS</a>
                <button type="button" className="cx-btn" onClick={() => done.mutate({ id: item.id, action: 'done' })}>Done</button>
                <button type="button" className="cx-btn" onClick={() => done.mutate({ id: item.id, action: 'snooze' })}>Later today</button>
              </div>
            </div>
            : current ? <TaskDetail key={current} id={current} row={tasks.find((x) => x.id === current)} onClosed={() => onSelect(null)} />
            : <Empty icon="approvals" title="Nothing is waiting on you">Claude puts new decisions here.</Empty>}
        </div>
      </div>
    </section>
  );
}

function TaskDetail({ id, row, onClosed }: { id: string; row: Task | undefined; onClosed: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const t = useQuery({ queryKey: ['pk-task', id], queryFn: () => api<TaskFull>(`/api/v1/ops/tasks/${id}`) });
  const h = useQuery({ queryKey: ['pk-handoffs', id], queryFn: () => api<Handoff[]>(`/api/v1/ops/tasks/${id}/handoffs`) });
  const open = h.data?.find((x) => x.status === 'pending' || x.status === 'claimed');
  const last = h.data?.find((x) => x.reply);
  async function act(kind: 'answer' | 'close' | 'claude') {
    if (kind !== 'close' && !text.trim()) return toast(<>{kind === 'claude' ? 'Write what the Pocket Agent should do first' : 'Write an answer first'}</>, 'fire');
    setBusy(true);
    try {
      if (kind === 'claude') await send('POST', `/api/v1/ops/tasks/${id}/handoff`, { note: text.trim() });
      else await send('POST', `/api/v1/ops/tasks/${id}/answer`, { body: text.trim(), close: kind === 'close' });
      toast(kind === 'claude' ? <>Sent to the Pocket Agent · its answer comes back to your inbox</> : kind === 'close' ? <>{text.trim() ? 'Answered and closed' : 'Closed'} · Tasks</> : <>Answer saved on the task</>);
      setText('');
      qc.invalidateQueries({ queryKey: ['pk-work'] }); qc.invalidateQueries({ queryKey: ['pk-task', id] }); qc.invalidateQueries({ queryKey: ['pk-handoffs', id] });
      if (kind === 'close') onClosed();
    } catch (e) { toast(<>{(e as Error).message}</>, 'fire'); } finally { setBusy(false); }
  }
  if (!t.data) return null;
  return (
    <div className="cx-stack" data-testid="pocket-task">
      <h2>{t.data.task.title}</h2>
      {t.data.task.description && <p className="pk-ask">{t.data.task.description}</p>}
      {t.data.comments.length > 0 && <div className="pk-thread">{t.data.comments.slice(-4).map((c) => <p key={c.id}><small>{formatDay(c.created_at)}</small>{c.body}</p>)}</div>}
      {row && <p className="pk-task-meta">{[row.list, row.due_on ? `Due ${formatDay(`${row.due_on}T12:00:00`)}` : null].filter(Boolean).join(' · ')}</p>}
      {open && <p className="cx-chip cx-chip--decision" style={{ alignSelf: 'start' }}>With the Pocket Agent since {formatDay(open.created_at)}: “{open.note.slice(0, 80)}”</p>}
      {last && !open && <div className="pk-reply"><span className="cx-label">The Pocket Agent replied</span><p>{last.reply}</p></div>}
      <textarea className="cx-textarea pk-answer" value={text} onChange={(e) => setText(e.target.value)} placeholder="An answer, or what the Pocket Agent should do. Tap the mic on the keyboard to dictate." data-testid="pocket-answer" />
      <div className="pk-actions">
        <button type="button" className="cx-btn cx-btn--primary" disabled={busy} onClick={() => act('close')} data-testid="pocket-answer-close">{text.trim() ? 'Answer and close' : 'Mark done'}</button>
        <button type="button" className="cx-btn pk-claude" disabled={busy || !!open} onClick={() => act('claude')} data-testid="pocket-handoff">Send to the Pocket Agent</button>
        <button type="button" className="cx-btn" disabled={busy || !text.trim()} onClick={() => act('answer')}>Save answer, keep open</button>
      </div>
      <p className="pk-hint">The Pocket Agent picks requests up within 5 minutes; its answer lands in Needs you.</p>
    </div>
  );
}

/**
 * A report reflows to the width it is given, but tables and charts have a floor (a weekly paid-media report needs ~614px).
 * Narrower than that, it is scaled down to fit — never read by scrolling sideways (Adam 2026-10-30: "the report doesn't
 * scale to fit the screen"). Returns the height the frame must be to show the whole report.
 */
function fitDoc(f: HTMLIFrameElement | null): number | null {
  const d = f?.contentDocument, r = d?.documentElement;
  if (!f || !r) return null;
  // Let the frame shrink first: a frame left at its old (taller) height pads scrollHeight, so rotating never gave it back.
  f.style.height = '0px';
  r.style.zoom = '';
  r.style.setProperty('-webkit-text-size-adjust', '100%');   // iOS enlarges text in landscape and keeps it after rotating back
  let z = 1;
  for (let i = 0; i < 3 && r.scrollWidth > f.clientWidth + 1; i++) { z *= f.clientWidth / r.scrollWidth; r.style.zoom = String(z); }
  r.style.overflowX = 'hidden';
  const h = Math.max(r.scrollHeight, Math.ceil(r.getBoundingClientRect().height * z));
  f.style.height = `${h}px`;
  return h;
}

/** Refit whenever the frame's width changes — rotation included. iOS reports the new width a beat after the turn. */
function useRefit(frame: React.RefObject<HTMLIFrameElement | null>, measure: () => void) {
  useEffect(() => {
    const f = frame.current; if (!f) return;
    let w = f.clientWidth, t = 0;
    const ro = new ResizeObserver(() => { if (f.clientWidth === w) return; w = f.clientWidth; cancelAnimationFrame(t); t = requestAnimationFrame(measure); });
    ro.observe(f);
    const turn = () => setTimeout(measure, 350);
    addEventListener('orientationchange', turn);
    return () => { ro.disconnect(); cancelAnimationFrame(t); removeEventListener('orientationchange', turn); };
  }, []);
}

type Pin = { x: number; y: number; text: string };
/**
 * The drawing layer covers the whole report, and a long report is ~10,000px tall. iPad Safari refuses a canvas over
 * ~16.7M pixels (it stays blank and the page stalls), so the layer's resolution drops until it fits in 8M.
 */
const INK_MAX_PX = 8_000_000;
const inkScale = (s: { w: number; h: number }) => Math.min(devicePixelRatio || 1, Math.sqrt(INK_MAX_PX / Math.max(1, s.w * s.h)));
type Stroke = { tool: 'pen' | 'hl'; color: string; pts: [number, number, number][] };
const INKS = ['#E5484D', '#2060EB', '#1B2430'];

/** The report, full height, with a drawing layer on top. Pencil always draws; a finger draws only with a tool picked. */
function Viewer({ id, org, report, onClose }: { id: string; org: Org; report: Report | undefined; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const frame = useRef<HTMLIFrameElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [tool, setTool] = useState<'scroll' | 'pen' | 'hl' | 'pin' | 'erase'>('scroll');
  const [ink, setInk] = useState(INKS[0]!);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const [pins, setPins] = useState<Pin[]>([]);
  const [hist, setHist] = useState<('s' | 'p')[]>([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const live = useRef<Stroke | null>(null);
  const sized = useRef<{ w: number; h: number } | null>(null);

  // Same origin, no scripts (sandbox without allow-scripts): the parent may measure it; the report can't run anything.
  const measure = () => {
    const h = fitDoc(frame.current); if (!h) return;
    const w = frame.current!.clientWidth;
    // Ink is in pixels; after a rotation the report is laid out at a new size, so marks scale with it.
    const old = sized.current; sized.current = { w, h };
    if (old && (old.w !== w || old.h !== h)) { const kx = w / old.w, ky = h / old.h; setStrokes((x) => x.map((st) => ({ ...st, pts: st.pts.map(([a, b, p]) => [a * kx, b * ky, p] as [number, number, number]) }))); }
    setSize({ w, h });
  };
  useRefit(frame, () => measure());

  const paint = useCallback(() => {
    const c = canvas.current; if (!c || !size) return;
    const ctx = c.getContext('2d')!; const k = inkScale(size);
    ctx.setTransform(k, 0, 0, k, 0, 0); ctx.clearRect(0, 0, size.w, size.h); ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    for (const s of [...strokes, ...(live.current ? [live.current] : [])]) {
      ctx.globalAlpha = s.tool === 'hl' ? 0.35 : 1; ctx.strokeStyle = s.tool === 'hl' ? '#FFD43B' : s.color;
      for (let i = 1; i < s.pts.length; i++) {
        const [x0, y0] = s.pts[i - 1]!, [x1, y1, p] = s.pts[i]!;
        ctx.lineWidth = s.tool === 'hl' ? 18 : 1.2 + p * 3.2; ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;
  }, [strokes, size]);
  useEffect(() => { const c = canvas.current; if (!c || !size) return; const k = inkScale(size); c.width = Math.round(size.w * k); c.height = Math.round(size.h * k); paint(); }, [size, paint]);

  const at = (e: React.PointerEvent): [number, number, number] => { const b = canvas.current!.getBoundingClientRect(); return [e.clientX - b.left, e.clientY - b.top, e.pressure || 0.5]; };
  const draws = (e: React.PointerEvent) => e.pointerType === 'pen' || tool !== 'scroll';
  function down(e: React.PointerEvent) {
    if (!draws(e) || !size) return;
    const p = at(e);
    if (tool === 'pin') { setPins((x) => [...x, { x: p[0] / size.w, y: p[1] / size.h, text: '' }]); setHist((x) => [...x, 'p']); return; }
    if (tool === 'erase') { setStrokes((x) => x.filter((s) => !s.pts.some(([sx, sy]) => Math.hypot(sx - p[0], sy - p[1]) < 16))); return; }
    canvas.current!.setPointerCapture(e.pointerId);
    live.current = { tool: tool === 'hl' ? 'hl' : 'pen', color: ink, pts: [p] };
  }
  function move(e: React.PointerEvent) { if (!live.current) return; for (const ev of e.nativeEvent.getCoalescedEvents?.() ?? [e.nativeEvent]) live.current.pts.push(at(ev as never)); paint(); }
  function up() { if (!live.current) return; const s = live.current; live.current = null; if (s.pts.length > 1) { setStrokes((x) => [...x, s]); setHist((x) => [...x, 's']); } }
  function undo() { const last = hist[hist.length - 1]; if (!last) return; setHist((x) => x.slice(0, -1)); if (last === 'p') setPins((x) => x.slice(0, -1)); else setStrokes((x) => x.slice(0, -1)); }

  /** The drawing alone, scaled to 900px wide: what Claude lays over the report to see what was marked. */
  function exportMarkup(): string | null {
    if (!strokes.length || !size || !canvas.current) return null;
    const w = 900, h = Math.round((size.h * w) / size.w), out = document.createElement('canvas');
    out.width = w; out.height = h; out.getContext('2d')!.drawImage(canvas.current, 0, 0, w, h);
    return out.toDataURL('image/png');
  }
  async function toClaude() {
    if (!strokes.length && !pins.length && !note.trim()) return toast(<>Mark something up or write a note first</>, 'fire');
    if (pins.some((p) => !p.text.trim())) return toast(<>Every numbered note needs a few words</>, 'fire');
    setBusy(true);
    try {
      await send('POST', `/api/v1/delivery/reports/${id}/revisions`, { note: note.trim(), pins: pins.map((p) => ({ ...p, text: p.text.trim() })), markup: exportMarkup() }, org);
      qc.invalidateQueries({ queryKey: ['pk-reports'] }); toast(<>Sent to Claude with {pins.length} note{pins.length === 1 ? '' : 's'} · the answer comes back to your inbox</>); onClose();
    } catch (e) { toast(<>{(e as Error).message}</>, 'fire'); } finally { setBusy(false); }
  }
  async function approve() {
    if (!report) return;
    if (!confirm(`Publish this report? ${report.account} will see it in their portal.`)) return;
    setBusy(true);
    try { await send('PATCH', `/api/v1/delivery/reports/${id}`, { status: 'published' }, org); qc.invalidateQueries({ queryKey: ['pk-reports'] }); toast(<>Published · {report.account} can see it</>); onClose(); }
    catch (e) { toast(<>{(e as Error).message}</>, 'fire'); } finally { setBusy(false); }
  }

  return (
    <div className="pk-viewer" role="dialog" aria-modal="true" aria-label="Report" data-testid="pocket-viewer">
      <div className="pk-vbar">
        <button type="button" className="cx-btn" onClick={onClose}>Done</button>
        <div className="pk-grow"><b>{report ? `${report.account} · ${reportName(report.type)}` : 'Report'}</b><small>{report?.period_label}</small></div>
        <div className="pk-seg pk-tools">
          {([['scroll', 'Scroll', 'M12 3v18M5 10l7-7 7 7'], ['pen', 'Pen', 'M4 20l4-1 11-11-3-3L5 16z'], ['hl', 'Highlighter', 'M9 15l8-8 3 3-8 8-4 1zM3 21h18'], ['pin', 'Numbered note', 'M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z'], ['erase', 'Eraser', 'M16 4l5 5-10 10H6l-3-3zM11 19h10']] as const).map(([k, l, d]) => (
            <button key={k} type="button" aria-pressed={tool === k} aria-label={l} title={l} onClick={() => setTool(k)}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg></button>))}
          <button type="button" aria-label="Undo" title="Undo" onClick={undo}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 14L4 9l5-5M4 9h10a6 6 0 0 1 0 12h-3" /></svg></button>
        </div>
        <div className="pk-inks">{INKS.map((c) => <button key={c} type="button" style={{ background: c }} aria-pressed={ink === c} aria-label={`Ink ${c}`} onClick={() => setInk(c)} />)}</div>
      </div>
      <div className="pk-vbody">
        <div className="pk-stage">
          <div className="pk-sheet" style={size ? { height: size.h } : undefined}>
            <iframe ref={frame} title="Report" src={inOrg(`/api/v1/delivery/reports/${id}/view`, org)} sandbox="allow-same-origin" scrolling="no" onLoad={measure} style={size ? { height: size.h } : undefined} />
            <canvas ref={canvas} className="pk-ink" data-tool={tool} style={size ? { width: size.w, height: size.h } : undefined}
              onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} />
            {size && pins.map((p, i) => <span key={i} className="pk-pin" style={{ left: `${p.x * 100}%`, top: p.y * size.h }}>{i + 1}</span>)}
          </div>
        </div>
        <aside className="pk-side">
          <h3>Your notes for Claude</h3>
          <div className="pk-notes">
            {!strokes.length && !pins.length && <p className="cx-none">Draw with Apple Pencil anywhere. With a finger, pick a tool first. The speech-bubble tool drops a numbered note on a spot.</p>}
            {strokes.length > 0 && <p className="cx-faint">{strokes.length} mark{strokes.length === 1 ? '' : 's'} drawn. Claude sees them over the page.</p>}
            {pins.map((p, i) => <label key={i} className="pk-note"><b>{i + 1}</b><textarea value={p.text} placeholder="What should change here?" autoFocus={i === pins.length - 1}
              onChange={(e) => setPins((x) => x.map((q, j) => (j === i ? { ...q, text: e.target.value } : q)))} /></label>)}
            <textarea className="cx-textarea" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything about the whole report" data-testid="pocket-viewer-note" />
          </div>
          <div className="pk-side-actions">
            {report?.revision ? <p className="cx-chip cx-chip--decision">Claude is working on changes to this one</p> : <>
              <button type="button" className="cx-btn pk-claude" disabled={busy} onClick={toClaude} data-testid="pocket-send-claude">Send markup back to Claude</button>
              {report && canPublish(report) && <button type="button" className="cx-btn cx-btn--primary" disabled={busy} onClick={approve} data-testid="pocket-approve">Approve and publish</button>}
            </>}
            <a className="cx-link-btn" href={inOrg(`/api/v1/delivery/reports/${id}/view`, org)} target="_blank" rel="noreferrer">Open to print or save as PDF</a>
          </div>
        </aside>
      </div>
    </div>
  );
}

const FIG = [{ k: 'leads', label: 'Leads', c: 'var(--cx-data-1)' }, { k: 'sessions', label: 'Sessions', c: 'var(--cx-data-2)' }, { k: 'clicks', label: 'Search clicks', c: 'var(--cx-data-4)' }, { k: 'spend', label: 'Ad spend', c: 'var(--cx-data-5)' }] as const;

const initials = (n: string) => n.replace(/[&,.()—-]/g, ' ').split(/\s+/).filter((w) => /^[A-Za-z0-9]/.test(w)).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '?';

/** The client's logo (moved over from old Harbor), or clean initials when there is none or it fails to load. */
function Logo({ name, src, size = 52 }: { name: string; src?: string | null | undefined; size?: number }) {
  const [broken, setBroken] = useState(false);
  return src && !broken
    ? <span className="pk-logo" style={{ width: size, height: size }}><img src={src} alt="" loading="lazy" onError={() => setBroken(true)} /></span>
    : <span className="pk-logo pk-logo--mono" style={{ width: size, height: size, fontSize: size * 0.33 }} aria-hidden="true">{initials(name)}</span>;
}

function Tile({ name, logo, line, onOpen, i }: { name: string; logo?: string | null | undefined; line: React.ReactNode; onOpen: () => void; i: number }) {
  return (
    <button type="button" className={`cx-panel pk-tile${i < 16 ? ' cx-rise' : ''}`} style={i < 16 ? delay(i, STAGGER.list) : undefined} onClick={onOpen} data-testid="pocket-tile">
      <Logo name={name} src={logo} />
      <span className="pk-tile-text"><b>{name}</b><small>{line}</small></span>
    </button>
  );
}

/** Clients as logo tiles, the ones with the most waiting first. Accounts with no documents at all fold away at the bottom. */
function ClientGrid({ accounts, reports, loaded, onOpen, title = 'Clients', sub = 'Pick a client to read their documents and see their numbers.' }: { accounts: Account[] | undefined; reports: Report[]; loaded: boolean; onOpen: (id: string) => void; title?: string; sub?: string }) {
  const [order, setOrder] = useState<'review' | 'az'>('review');
  const rows = useMemo(() => {
    const by = new Map<string, { all: number; review: number }>();
    for (const r of reports) { const x = by.get(r.account_id) ?? { all: 0, review: 0 }; x.all++; if (toReview(r)) x.review++; by.set(r.account_id, x); }
    return (accounts ?? []).filter((a) => a.status !== 'prospect' && a.status !== 'churned')
      .map((a) => ({ a, ...(by.get(a.id) ?? { all: 0, review: 0 }) }))
      .sort((x, y) => (order === 'review' ? y.review - x.review || y.all - x.all : 0) || x.a.name.localeCompare(y.a.name));
  }, [accounts, reports, order]);
  const waiting = rows.filter((r) => r.review), rest = rows.filter((r) => !r.review && r.all), none = rows.filter((r) => !r.all);
  const tile = (r: (typeof rows)[number], i: number) => <Tile key={r.a.id} i={i} name={r.a.name} logo={r.a.logo} onOpen={() => onOpen(r.a.id)}
    line={<>{r.review > 0 && <em>{r.review} to review · </em>}{r.all ? `${r.all} document${r.all === 1 ? '' : 's'}` : 'No documents yet'}</>} />;
  return (
    <section className="cx-stack">
      <header className="pk-head"><div><h1>{title}</h1><p className="pk-sub">{sub}</p></div>
        <div className="pk-seg">{([['review', 'Most to review'], ['az', 'A–Z']] as const).map(([k, l]) => <button key={k} type="button" aria-pressed={order === k} onClick={() => setOrder(k)}>{l}</button>)}</div></header>
      {loaded && !rows.length && <Empty icon="register" title="No clients yet" />}
      {order === 'review' ? <>
        {waiting.length > 0 && <><h2 className="pk-sect">Waiting on you <span>{waiting.length}</span></h2><div className="pk-tiles">{waiting.map(tile)}</div></>}
        {rest.length > 0 && <><h2 className="pk-sect">Nothing to review <span>{rest.length}</span></h2><div className="pk-tiles">{rest.map((r, i) => tile(r, i + waiting.length))}</div></>}
      </> : <div className="pk-tiles">{rows.filter((r) => r.all).map(tile)}</div>}
      {none.length > 0 && <details className="pk-none">
        <summary>No documents yet · {none.length} client{none.length === 1 ? '' : 's'}</summary>
        <div className="pk-tiles">{none.map((r) => tile(r, 99))}</div>
      </details>}
    </section>
  );
}

const dealName = (d: Deal) => d.organization?.trim() || d.title.replace(/\s+—\s+.*$/, '');
const dealValue = (d: Deal) => (Number(d.monthly) ? `${money(Number(d.monthly))}/mo` : Number(d.value) ? money(Number(d.value)) : Number(d.one_off) ? money(Number(d.one_off)) : null);

/** Prospects: every open deal, furthest along first — as tiles, or as the pipeline by stage (Adam 2026-10-31: "I want to see my pipeline"). */
function ProspectGrid({ deals, accounts, reports, loaded, onOpen }: { deals: Deal[] | undefined; accounts: Account[]; reports: Report[]; loaded: boolean; onOpen: (id: string) => void }) {
  const [view, setView] = useState<'deals' | 'pipeline'>(() => { try { return localStorage.getItem('caios.pocket.prospects') === 'pipeline' ? 'pipeline' : 'deals'; } catch { return 'deals'; } });
  const pick = (v: 'deals' | 'pipeline') => { setView(v); try { localStorage.setItem('caios.pocket.prospects', v); } catch { /* storage blocked */ } };
  const docsOf = (d: Deal) => (d.account_id ? reports.filter((r) => r.account_id === d.account_id).length : 0) + (d.files ?? 0);
  const line = (d: Deal, stage = true) => { const v = dealValue(d), n = docsOf(d);
    return <>{stage && <><span className="pk-stage-dot" style={{ background: d.stage_color }} />{d.stage}</>}{[stage ? '' : null, v, n ? `${n} doc${n === 1 ? '' : 's'}` : null].filter((x) => x !== null).join(' · ') || (stage ? '' : 'No value yet')}</>; };
  return (
    <section className="cx-stack">
      <header className="pk-head"><div><h1>Prospects</h1><p className="pk-sub">{view === 'deals' ? 'Open deals, furthest along first. Tap one for the deal and any documents made for them.' : 'Every open deal by stage, with what each stage is worth.'}</p></div>
        <div className="pk-seg">{([['deals', 'Deals'], ['pipeline', 'Pipeline']] as const).map(([k, l]) => <button key={k} type="button" aria-pressed={view === k} onClick={() => pick(k)} data-testid={`pocket-prospects-${k}`}>{l}</button>)}</div></header>
      {loaded && !deals?.length && <Empty icon="passage" title="No open deals">New deals from Sales show here.</Empty>}
      {view === 'deals'
        ? <div className="pk-tiles">{(deals ?? []).map((d, i) => <Tile key={d.id} i={i} name={dealName(d)} logo={accounts.find((a) => a.id === d.account_id)?.logo} onOpen={() => onOpen(d.id)} line={line(d)} />)}</div>
        : <Pipeline deals={deals ?? []} line={(d) => line(d, false)} onOpen={onOpen} />}
    </section>
  );
}

/** The pipeline: a column per stage on the iPad, stacked on a phone; each stage shows its count and its monthly and one-off value. */
function Pipeline({ deals, line, onOpen }: { deals: Deal[]; line: (d: Deal) => React.ReactNode; onOpen: (id: string) => void }) {
  const stages = useMemo(() => {
    const by = new Map<string, { name: string; color: string; pos: number; deals: Deal[] }>();
    for (const d of deals) { const k = d.stage; const x = by.get(k) ?? { name: k, color: d.stage_color, pos: d.stage_position ?? 0, deals: [] }; x.deals.push(d); by.set(k, x); }
    return [...by.values()].sort((a, b) => b.pos - a.pos);
  }, [deals]);
  const total = (ds: Deal[], k: 'monthly' | 'one_off') => ds.reduce((a, d) => a + (Number(d[k]) || (k === 'one_off' && !Number(d.monthly) ? Number(d.value) || 0 : 0)), 0);
  const worth = (ds: Deal[]) => [total(ds, 'monthly') ? `${money(total(ds, 'monthly'))}/mo` : null, total(ds, 'one_off') ? `${money(total(ds, 'one_off'))} one-off` : null].filter(Boolean).join(' + ') || 'No value yet';
  return (
    <>
      <div className="pk-stats pk-pipe-sum">
        <div className="cx-stat pk-stat" style={{ ['--c' as string]: 'var(--cx-data-1)' }}><div className="cx-label">Open deals</div><div className="cx-value">{deals.length}</div></div>
        <div className="cx-stat pk-stat" style={{ ['--c' as string]: 'var(--cx-data-3)' }}><div className="cx-label">Monthly, if all close</div><div className="cx-value">{money(total(deals, 'monthly'))}</div></div>
        <div className="cx-stat pk-stat" style={{ ['--c' as string]: 'var(--cx-data-5)' }}><div className="cx-label">One-off, if all close</div><div className="cx-value">{money(total(deals, 'one_off'))}</div></div>
      </div>
      <div className="pk-pipe" data-testid="pocket-pipeline">
        {stages.map((st) => (
          <section key={st.name} className="cx-panel pk-pipe-col" style={{ ['--stage' as string]: st.color }}>
            <header><b>{st.name}</b><span className="pk-pipe-n">{st.deals.length}</span><small>{worth(st.deals)}</small></header>
            {st.deals.map((d) => (
              <button key={d.id} type="button" className="pk-pipe-deal" onClick={() => onOpen(d.id)} data-testid="pocket-pipe-deal">
                <b>{dealName(d)}</b><small>{line(d)}</small>
                {d.next_action && <small className="pk-pipe-next">Next: {d.next_action}{d.next_action_at ? ` · ${formatDay(d.next_action_at)}` : ''}</small>}
              </button>))}
          </section>))}
      </div>
    </>
  );
}

/**
 * Proposals, audits and plans made for a prospect, filed on its deal (Adam 2026-10-31). On a deal they can be added; on
 * a client the ones from when it was a prospect carry over. A PDF opens in the device's own viewer, which fits the screen.
 */
function DealFiles({ dealId, accountId, title = 'Proposals and audits' }: { dealId?: string; accountId?: string; title?: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const key = ['pk-files', dealId ?? `a:${accountId}`];
  const files = useQuery({ queryKey: key, queryFn: () => api<DealFile[]>(dealId ? `/api/v1/sales/deals/${dealId}/files` : `/api/v1/sales/files?accountId=${accountId}`) });
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  async function add(list: FileList | null) {
    if (!dealId || !list?.length) return;
    setBusy(true);
    try {
      let added = 0;
      for (const f of Array.from(list)) {
        const data = await new Promise<string>((ok, no) => { const r = new FileReader(); r.onload = () => ok(String(r.result).split(',')[1] ?? ''); r.onerror = () => no(r.error); r.readAsDataURL(f); });
        const r = await send<{ added: boolean }>('POST', `/api/v1/sales/deals/${dealId}/files`, { filename: f.name, mime: f.type, data });
        if (r.added) added++;
      }
      toast(<>{added ? `Added ${added} document${added === 1 ? '' : 's'}` : 'Already on this deal'}</>);
      qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ['pk-deals'] });
    } catch (e) { toast(<>{(e as Error).message}</>, 'fire'); } finally { setBusy(false); if (input.current) input.current.value = ''; }
  }
  if (!dealId && !files.data?.length) return null;
  return (
    <section className="cx-panel pk-card pk-files" data-testid="pocket-files">
      <div className="pk-card-head"><h2>{title}</h2>
        {dealId && <><button type="button" className="cx-btn" disabled={busy} onClick={() => input.current?.click()} data-testid="pocket-file-add">{busy ? 'Adding…' : 'Add a PDF'}</button>
          <input ref={input} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" multiple hidden onChange={(e) => void add(e.target.files)} /></>}</div>
      {files.data?.length ? files.data.map((f) => (
        <a key={f.id} className="pk-row pk-file" href={`/api/v1/sales/files/${f.id}`} target="_blank" rel="noreferrer" data-testid="pocket-file">
          <span className="pk-file-icon" data-mime={f.mime === 'application/pdf' ? 'pdf' : 'img'}>{f.mime === 'application/pdf' ? 'PDF' : 'IMG'}</span>
          <span className="pk-grow"><b>{f.title}</b><small>{formatDay(f.created_at)} · {f.size > 1048576 ? `${(f.size / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(f.size / 1024))} KB`}{f.deal && !dealId ? ` · from ${f.deal}` : ''}</small></span>
        </a>)) : files.isSuccess && <p className="cx-none">Nothing filed yet. Documents Claude makes for this prospect land here.</p>}
    </section>
  );
}

function Back({ label, onBack, name, logo, children }: { label: string; onBack: () => void; name: string; logo?: string | null | undefined; children?: React.ReactNode }) {
  return (
    <header className="pk-chead">
      <button type="button" className="cx-btn" onClick={onBack} data-testid="pocket-back">‹ {label}</button>
      <Logo name={name} src={logo} size={44} />
      <h1>{name}</h1>
      <span className="pk-spacer" />{children}
    </header>
  );
}

function ClientPage({ a, view, setView, docs, c, days, setDays, onBack, onReport, org = null, backLabel = 'Clients' }: { org?: Org; backLabel?: string; a: Account | undefined; view: 'docs' | 'numbers'; setView: (v: 'docs' | 'numbers') => void; docs: Report[];
  c: Standing | undefined; days: number; setDays: (n: number) => void; onBack: () => void; onReport: (id: string) => void }) {
  if (!a) return null;
  return (
    <section className="pk-page">
      <Back label={backLabel} onBack={onBack} name={a.name} logo={a.logo}>
        <div className="pk-seg">{([['docs', `Documents${docs.length ? ` (${docs.length})` : ''}`], ['numbers', 'Numbers']] as const).map(([k, l]) =>
          <button key={k} type="button" aria-pressed={view === k} onClick={() => setView(k)} data-testid={`pocket-view-${k}`}>{l}</button>)}</div>
      </Back>
      {view === 'docs' ? <>{!org && <DealFiles accountId={a.id} title="From when they were a prospect" />}<DocsSplit docs={docs} onReport={onReport} org={org} /></> : <Numbers c={c} days={days} setDays={setDays} />}
    </section>
  );
}

function ProspectPage({ d, a, docsOf, onBack, onReport }: { d: Deal | undefined; a: Account | undefined; docsOf: (id: string | null | undefined) => Report[]; onBack: () => void; onReport: (id: string) => void }) {
  if (!d) return null;
  const docs = docsOf(d.account_id), v = dealValue(d);
  const facts = [['Stage', <><span className="pk-stage-dot" style={{ background: d.stage_color }} />{d.stage} · since {formatDay(d.stage_changed_at)}</>], ['Value', v], ['Contact', d.contact_name],
    ['Next step', d.next_action ? `${d.next_action}${d.next_action_at ? ` · ${formatDay(d.next_action_at)}` : ''}` : null], ['Expected to close', d.expected_close_on ? formatDay(`${d.expected_close_on}T12:00:00`) : null],
    ['Website', d.website ? <a href={/^https?:/.test(d.website) ? d.website : `https://${d.website}`} target="_blank" rel="noreferrer">{d.website.replace(/^https?:\/\//, '')}</a> : null]] as const;
  return (
    <section className="pk-page">
      <Back label="Prospects" onBack={onBack} name={dealName(d)} logo={a?.logo}><a className="cx-btn" href="/sales/deals">Open in Deals</a></Back>
      <div className="cx-panel pk-card pk-facts">{facts.filter(([, x]) => x).map(([k, x]) => <div key={k}><span className="cx-label">{k}</span><span>{x}</span></div>)}</div>
      <DealFiles dealId={d.id} />
      {docs.length > 0 && <DocsSplit docs={docs} onReport={onReport} />}
    </section>
  );
}

/** One client's documents: a tidy list on the left, the open one on the right with its buttons under it (List + preview). */
function DocsSplit({ docs, onReport, org = null }: { docs: Report[]; onReport: (id: string) => void; org?: Org }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [filter, setFilter] = useState<'all' | 'review' | DocKind>(() => (docs.some(toReview) ? 'review' : 'all'));
  const list = docs.filter((r) => filter === 'all' || (filter === 'review' ? toReview(r) : docKind(r.type) === filter));
  const [sel, setSel] = useState<string | null>(null);
  const r = list.find((x) => x.id === sel) ?? list[0];
  const [busy, setBusy] = useState(false);
  async function approve() {
    if (!r || !confirm(`Publish this report? ${r.account} will see it in their portal.`)) return;
    setBusy(true);
    try { await send('PATCH', `/api/v1/delivery/reports/${r.id}`, { status: 'published' }, org); qc.invalidateQueries({ queryKey: ['pk-reports'] }); toast(<>Published · {r.account} can see it</>); }
    catch (e) { toast(<>{(e as Error).message}</>, 'fire'); } finally { setBusy(false); }
  }
  const kinds = (['onboarding', 'sales', 'report'] as const).filter((k) => docs.some((x) => docKind(x.type) === k));
  return (
    <div className="pk-docsplit">
      <div className="pk-seg pk-docfilter">{([['all', 'All'], ['review', 'To review'], ...kinds.map((k) => [k, KIND_LABEL[k]] as const)] as const).map(([k, l]) =>
        <button key={k} type="button" aria-pressed={filter === k} onClick={() => { setFilter(k); setSel(null); }}>{l}</button>)}</div>
      <div className="cx-panel pk-doclist">
        {list.length ? list.map((x) => (
          <button key={x.id} type="button" className="pk-docrow" data-kind={docKind(x.type)} aria-selected={x.id === r?.id} onClick={() => { setSel(x.id); onReport(x.id); }} data-testid="pocket-doc">
            <span className="pk-dot" />
            <span className="pk-grow"><b>{reportName(x.type)}</b><small>{shortPeriod(x.period_label)}{x.audience === 'team' ? ' · team' : ''}</small></span>
            <Status r={x} />
          </button>)) : <p className="cx-none">{filter === 'review' ? 'Nothing here is waiting on you.' : 'Nothing of this kind.'}</p>}
      </div>
      {r ? <div className="cx-panel pk-preview" data-testid="pocket-preview">
        <div className="pk-preview-head"><div className="pk-grow"><b>{reportName(r.type)}</b><small>{r.period_label}</small></div><Status r={r} /></div>
        <Preview key={r.id} id={r.id} org={org} onOpen={() => onReport(r.id)} />
        <div className="pk-actions pk-preview-actions">
          <button type="button" className="cx-btn cx-btn--primary" onClick={() => onReport(r.id)} data-testid="pocket-markup">Open and mark up</button>
          {!r.revision && <button type="button" className="cx-btn pk-claude" onClick={() => onReport(r.id)}>Send back to Claude</button>}
          {canPublish(r) && <button type="button" className="cx-btn" disabled={busy} onClick={approve} data-testid="pocket-approve">Approve and publish</button>}
        </div>
      </div> : <div className="cx-panel pk-preview pk-preview--empty"><p className="cx-none">Pick a document on the left.</p></div>}
    </div>
  );
}

/**
 * The report itself, read-only, sized to its own height inside a scrolling box: an iPad scrolls a box reliably, an iframe
 * not always. Same origin and no scripts, as in the viewer.
 */
function Preview({ id, org, onOpen }: { id: string; org: Org; onOpen: () => void }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [h, setH] = useState<number | null>(null);
  const measure = () => setH(fitDoc(frame.current));
  useRefit(frame, () => measure());
  return (
    <div className="pk-preview-page">
      <button type="button" className="pk-preview-open" onClick={onOpen} aria-label="Open the document" data-testid="pocket-preview-open" />
      <iframe ref={frame} title="Document preview" src={inOrg(`/api/v1/delivery/reports/${id}/view`, org)} sandbox="allow-same-origin" scrolling="no" onLoad={measure} style={h ? { height: h } : undefined} />
    </div>
  );
}

function Numbers({ c, days, setDays }: { c: Standing | undefined; days: number; setDays: (n: number) => void }) {
  const SRC: Record<string, string> = { ga4: 'Analytics', gsc: 'Search Console', google_ads: 'Google Ads', meta_ads: 'Meta Ads', outcomes: 'Site leads', gbp: 'Business Profile', social: 'Social', clarity: 'Clarity', email: 'Email', aeo: 'AI search' };
  const range = <div className="pk-seg" style={{ alignSelf: 'start' }}>{[7, 30, 90].map((n) => <button key={n} type="button" aria-pressed={days === n} onClick={() => setDays(n)}>{n} days</button>)}</div>;
  if (!c || !c.sources.length) return <div className="cx-stack">{range}<Empty icon="register" title="No numbers in this window">Nothing from Analytics, Search Console, Ads or the site’s own leads has synced for these days.</Empty></div>;
  const tiles = [
    { label: 'Leads', v: c.now.leads, p: c.previous.leads, c: 'var(--cx-data-1)', note: c.leadsFrom === 'ga4' ? 'GA4 key events (no lead tracking yet)' : c.leadsFrom === 'site' ? 'forms, calls and bookings on the site' : 'no lead tracking connected' },
    { label: 'Sessions', v: c.now.sessions, p: c.previous.sessions, c: 'var(--cx-data-2)', note: 'Google Analytics' },
    { label: 'Search clicks', v: c.now.clicks, p: c.previous.clicks, c: 'var(--cx-data-4)', note: c.now.impressions !== null ? `${Math.round(c.now.impressions).toLocaleString()} impressions · Search Console` : 'Search Console' },
    { label: 'Ad spend', v: c.now.spend, p: c.previous.spend, c: 'var(--cx-data-5)', money: true, note: c.now.spend && c.now.adConversions ? `${money(c.now.spend / c.now.adConversions)} per ad conversion` : 'Google and Meta Ads' },
  ];
  return (
    <div className="cx-stack">
      <div className="pk-head"><p className="pk-sub">Last {days} days{c.syncedAt ? ` · synced ${formatDay(c.syncedAt)}` : ''}</p>{range}</div>
      <div className="pk-stats">{tiles.map((t, i) => (
        <div key={t.label} className="cx-stat cx-rise" style={{ ['--c' as string]: t.c, ...delay(i, STAGGER.stats) }}>
          <div className="cx-label">{t.label}</div><div className="cx-value">{t.v === null ? '—' : <CountUp value={t.v} {...(t.money ? { format: money } : {})} />}</div>
          {t.v !== null && <Delta now={t.v} before={t.p ?? 0} against="before" />}<small className="pk-source">{t.note}</small>
        </div>))}</div>
      <section className="cx-panel pk-card">
        <div className="pk-card-head"><h2>Day by day</h2></div>
        <div className="pk-lines">{FIG.map((f) => (
          <div key={f.k}><span className="cx-label">{f.label}</span>{c.series[f.k].some((x) => x) ? <Spark values={c.series[f.k].map((x) => x ?? 0)} width={900} height={56} color={f.c} /> : <span className="cx-faint">Not measured</span>}</div>))}
        </div>
        <p className="cx-label" style={{ marginTop: 10 }}>Connected: {c.sources.map((s) => SRC[s] ?? s).join(', ')}</p>
      </section>
    </div>
  );
}

/** One white-label partner's org, read beside ARC's: its accounts and documents, for the rail and its own section. */
function usePartner(org: string) {
  const accounts = useQuery({ queryKey: ['pk-accounts', org], staleTime: 5 * 60_000,
    queryFn: async () => (await api<Account[]>(inOrg('/api/v1/clients', org))).map((a) => ({ ...a, logo: a.logo && inOrg(a.logo, org) })) });
  const reports = useQuery({ queryKey: ['pk-reports', org], queryFn: () => api<Report[]>(inOrg('/api/v1/delivery/reports', org)), refetchInterval: 60_000 });
  return { accounts, reports };
}

function PartnerRail({ p, first, current, onGo }: { p: Partner; first: boolean; current: boolean; onGo: () => void }) {
  const { accounts, reports } = usePartner(p.id);
  const self = accounts.data?.find((a) => same(a.name, p.name));
  const n = reports.data?.filter(toReview).length ?? 0;
  return (
    <div className="pk-rail-item">
      {first && <hr className="pk-rail-rule" />}
      <button type="button" className="pk-rail-btn pk-rail-partner cx-rail-in" aria-current={current} aria-label={p.name} onClick={onGo} data-testid="pocket-tab-partner">
        <Logo name={p.name} src={self?.logo} size={40} />{n > 0 && <span className="pk-badge" data-tone="decision">{n}</span>}
      </button>
      <span className="pk-rail-label">{p.name.replace(/\s*Media$/i, '')}</span>
    </div>
  );
}

/** A partner's section: their clients as tiles, each opening like one of ARC's (Adam 2026-10-30: "6 Hands Media, their clients, and then Live Echo and their clients"). */
function PartnerTab({ p, days, setDays, onReport }: { p: Partner; days: number; setDays: (n: number) => void; onReport: (id: string) => void }) {
  const { accounts, reports } = usePartner(p.id);
  const [open, setOpen] = useState<{ id: string; view: 'docs' | 'numbers' } | null>(null);
  const win = useMemo(() => windowOf(days), [days]);
  const standings = useQuery({ queryKey: ['pk-standings', p.id, win.from, win.to], enabled: !!open,
    queryFn: () => api<Standings>(inOrg(`/api/v1/delivery/standings?from=${win.from}&to=${win.to}`, p.id)) });
  if (open) return <ClientPage key={open.id} org={p.id} backLabel={p.name} a={accounts.data?.find((a) => a.id === open.id)} view={open.view} setView={(view) => setOpen({ ...open, view })}
    docs={(reports.data ?? []).filter((r) => r.account_id === open.id)} c={standings.data?.clients.find((x) => x.id === open.id)} days={days} setDays={setDays} onBack={() => setOpen(null)} onReport={onReport} />;
  return <ClientGrid title={p.name} sub={`White-label partner. ${p.name}’s clients, under their brand.`} accounts={accounts.data} reports={reports.data ?? []}
    loaded={accounts.isSuccess && reports.isSuccess} onOpen={(id) => setOpen({ id, view: 'docs' })} />;
}
